import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { propertyAPI, getUploadUrl } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { isBackofficeRole } from '../lib/roles';
import { Button } from '../components/ui/button';
import { GlassCard } from '../components/common/GlassCard';
import {
  Building2,
  MapPin,
  Users,
  Award,
  ArrowRight,
  ChevronRight,
  Shield,
  Phone,
  Mail,
  Briefcase,
} from 'lucide-react';

const Landing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);

  useEffect(() => {
    propertyAPI.getProperties().then((res) => setProperties(res.data.slice(0, 4))).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      if (isBackofficeRole(user.role)) {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    }
  }, [user, navigate]);

  const features = [
    {
      icon: Building2,
      title: 'Premium Listings',
      description: 'Access exclusive property listings from top developers in Johor Bahru',
    },
    {
      icon: Users,
      title: 'Expert Agents',
      description: 'Connect with certified real estate professionals with 10+ years experience',
    },
    {
      icon: Award,
      title: 'Earn Rewards',
      description: 'Get cashback and commission on successful referrals',
    },
    {
      icon: Shield,
      title: 'Secure Platform',
      description: 'KYC verified transactions and secure payment processing',
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0A1628' }} data-testid="landing-page">
      {/* Hero Section */}
      <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?crop=entropy&cs=srgb&fm=jpg&q=85)',
          }}
        />
        <div className="absolute inset-0 hero-gradient" />

        {/* Header */}
        <header className="absolute top-0 left-0 right-0 z-10">
          <div className="container mx-auto px-6 py-5">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold text-white">SAM Rumah</span>
              </Link>
              <div className="flex items-center gap-3">
                <Link to="/browse-properties">
                  <Button variant="ghost" className="text-white hover:text-blue-400 hover:bg-white/5">
                    Properties
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="ghost" className="text-white hover:text-blue-400 hover:bg-white/5" data-testid="login-link">
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-blue-500 hover:bg-blue-600 text-white rounded-full px-6 btn-glow" data-testid="register-link">
                    Get Started
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 mb-6 animate-fade-in">
            <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></span>
            <span className="text-sm text-blue-400 font-medium">Your Trusted Property Partner</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 animate-fade-in">
            Discover Your
            <span className="text-gradient-brand"> Dream Property</span>
          </h1>
          <p className="text-base lg:text-lg text-slate-300 mb-8 max-w-2xl mx-auto animate-fade-in animate-delay-100">
            SAM Rumah Sdn Bhd - Malaysia's premier real estate platform with over 10 years of experience. 
            Expert guidance for buying and selling properties in Johor Bahru.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in animate-delay-200">
            <Link to="/register">
              <Button size="lg" className="bg-blue-500 hover:bg-blue-600 text-white rounded-full px-8 btn-glow w-full sm:w-auto" data-testid="hero-get-started">
                Get Started <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/browse-properties">
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-full px-8 w-full sm:w-auto">
                Browse Properties
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="absolute bottom-0 left-0 right-0 pb-12">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {[
                { value: '10+', label: 'Years Experience' },
                { value: '900+', label: 'Expert Agents' },
                { value: 'RM 2B+', label: 'Sales Volume' },
                { value: '10K+', label: 'Happy Clients' },
              ].map((stat, idx) => (
                <div
                  key={idx}
                  className="glass-card rounded-2xl p-4 text-center animate-fade-in"
                  style={{ animationDelay: `${300 + idx * 100}ms` }}
                >
                  <p className="text-2xl font-bold text-blue-400 font-mono">{stat.value}</p>
                  <p className="text-sm text-slate-400">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* About Section */}
      <section className="py-20 px-6" style={{ backgroundColor: '#0F172A' }}>
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-4">About SAM Rumah</h2>
              <p className="text-slate-400 mb-6 leading-relaxed">
                SAM Rumah SDN BHD is a registered real estate company in Malaysia, providing fast and reliable services citywide. With over 10 years of experience, our founders bring a significant edge in acquiring, disposing, and leasing properties.
              </p>
              <p className="text-slate-400 mb-8 leading-relaxed">
                Supported by a skilled team, we focus on value-added services and expertise to ensure customer satisfaction.
              </p>
              <Link to="/register?role=agent">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8 btn-glow-orange">
                  <Briefcase className="mr-2 w-5 h-5" />
                  Join Our Team
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-3xl overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?crop=entropy&cs=srgb&fm=jpg&q=85" 
                  alt="Real Estate Team"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-blue-500 text-white p-6 rounded-2xl shadow-xl">
                <p className="text-4xl font-bold">10+</p>
                <p className="text-sm opacity-90">Years of Excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6" style={{ backgroundColor: '#0A1628' }}>
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Why Choose SAM Rumah</h2>
            <p className="text-slate-400">Everything you need for your property journey</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <GlassCard key={idx} hover className="text-center animate-fade-in" style={{ animationDelay: `${idx * 100}ms` }}>
                  <div className="w-14 h-14 rounded-2xl bg-blue-500/10 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-7 h-7 text-blue-400" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-sm text-slate-400">{feature.description}</p>
                </GlassCard>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      {properties.length > 0 && (
        <section className="py-20 px-6" style={{ backgroundColor: '#0F172A' }}>
          <div className="container mx-auto max-w-6xl">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-3xl font-bold text-white mb-2">Latest Projects</h2>
                <p className="text-slate-400">Explore our premium property listings</p>
              </div>
              <Link to="/browse-properties">
                <Button variant="ghost" className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 rounded-full">
                  View All <ChevronRight className="ml-1 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {properties.map((property, idx) => (
                <Link key={property.id} to={`/properties/${property.id}`}>
                  <GlassCard hover className="overflow-hidden p-0 animate-fade-in" style={{ animationDelay: `${idx * 100}ms` }}>
                    <div className="relative">
                      <div
                        className="h-48 bg-cover bg-center"
                        style={{
                          backgroundImage: property.images?.[0]
                            ? `url(${getUploadUrl(property.images[0])})`
                            : 'url(https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85)',
                        }}
                      />
                      <div className="absolute inset-0 property-image-overlay" />
                      <div className="absolute top-3 left-3">
                        <span className="tag-blue">{property.property_type}</span>
                      </div>
                      <div className="absolute bottom-3 left-3">
                        <span className="tag-orange">Sales</span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-base font-semibold text-white mb-2 line-clamp-1">{property.name}</h3>
                      <div className="flex items-center gap-1 text-slate-400 text-sm mb-3">
                        <MapPin className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{property.location}</span>
                      </div>
                      <div className="flex items-center justify-between pt-3 border-t border-slate-700">
                        <p className="text-blue-400 font-mono font-semibold text-sm">
                          {formatCurrency(property.price_range_min)}
                        </p>
                        <span className="text-xs text-slate-500">{property.total_units} units</span>
                      </div>
                    </div>
                  </GlassCard>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 px-6 relative overflow-hidden" style={{ backgroundColor: '#0A1628' }}>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/20 to-orange-900/20" />
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <h2 className="text-3xl font-bold text-white mb-4">Connect With Us Now</h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            Whether you're looking to kickstart your career in real estate or find your dream home, 
            we're here for you. Reach out to us today and let's make your goals a reality.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-blue-500 hover:bg-blue-600 text-white rounded-full px-8 btn-glow w-full sm:w-auto cursor-default">
              Register as Buyer
            </Button>
            <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8 btn-glow-orange w-full sm:w-auto cursor-default">
              <Award className="mr-2 w-5 h-5" />
              Join as Agent
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-slate-800" style={{ backgroundColor: '#0F172A' }}>
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <span className="font-bold text-white">SAM Rumah</span>
              </div>
              <p className="text-sm text-slate-400">
                Your trusted property partner in Johor Bahru with over 10 years of experience.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/browse-properties" className="hover:text-blue-400 transition-colors">Properties</Link></li>
                <li><Link to="/register" className="hover:text-blue-400 transition-colors">Register</Link></li>
                <li><Link to="/login" className="hover:text-blue-400 transition-colors">Login</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>Property Sales</li>
                <li>Agent Network</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Contact Us</h4>
              <ul className="space-y-3 text-sm text-slate-400">
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-blue-400" />
                  <span>Admin@SAMRumah.com</span>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-blue-400" />
                  <span>+607 336 5705</span>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                  <span>Unit 1.07 & 1.08, Plaza DNP, Level 1, Johor Bahru</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">
              © 2026 SAM Rumah Sdn Bhd. All rights reserved.
            </p>
            <div className="flex items-center gap-4 text-sm text-slate-500">
              <span>EN</span>
              <span>|</span>
              <span>ZH</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
